import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../hooks/useUserData';
import InvoiceModal from '../components/InvoiceModal';
import AgreementModal from '../components/AgreementModal';
import CreateOptionsModal from '../components/CreateOptionsModal';
import {
  DocumentTextIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  CalendarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { db, auth } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';

const DashboardPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { stats = {}, documents = {}, loading = {} } = useUserData();
  const clients = documents.clients || [];
  const [activeTab, setActiveTab] = useState('invoices');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sortBy, setSortBy] = useState('date'); // date, amount, name
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter and sort invoices
  const filteredInvoices = useMemo(() => {
    let result = documents.invoices || [];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv =>
        (inv.clientName || '').toLowerCase().includes(query) ||
        (inv.description || '').toLowerCase().includes(query)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);

      switch (sortBy) {
        case 'oldest':
          return dateA - dateB;
        case 'amount':
          return (b.total || b.price || b.amount || 0) - (a.total || a.price || a.amount || 0);
        case 'amount-low':
          return (a.total || a.price || a.amount || 0) - (b.total || b.price || b.amount || 0);
        case 'name':
          return (a.clientName || '').localeCompare(b.clientName || '');
        case 'name-desc':
          return (b.clientName || '').localeCompare(a.clientName || '');
        case 'status-paid':
          if (a.status === 'paid' && b.status !== 'paid') return -1;
          if (a.status !== 'paid' && b.status === 'paid') return 1;
          return dateB - dateA;
        case 'status-due':
          if (a.status !== 'paid' && b.status === 'paid') return -1;
          if (a.status === 'paid' && b.status !== 'paid') return 1;
          return dateB - dateA;
        case 'date':
        default:
          return dateB - dateA;
      }
    });

    return result;
  }, [documents.invoices, searchQuery, sortBy]);

  // Selection handlers
  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  // eslint-disable-next-line no-unused-vars
const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.id)));
    }
  };

  const deleteSelectedInvoices = async () => {
    if (selectedInvoices.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedInvoices.size} invoice${selectedInvoices.size > 1 ? 's' : ''}? This cannot be undone.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedInvoices).map(invoiceId =>
        deleteDoc(doc(db, 'invoices', invoiceId))
      );
      await Promise.all(deletePromises);
      setSelectedInvoices(new Set());
    } catch (error) {
      console.error('Error deleting invoices:', error);
      alert('Failed to delete some invoices. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400 text-sm">Sign in to continue</p>
      </div>
    );
  }

  const quickActions = [
    { icon: DocumentTextIcon, label: 'Invoice', action: () => setShowInvoiceModal(true) },
    { icon: UserGroupIcon, label: 'Clients', action: () => navigate('/app/clients') },
    { icon: DocumentDuplicateIcon, label: 'Contracts', action: () => setShowAgreementModal(true) },
    { icon: CalendarIcon, label: 'Booking', action: () => navigate('/app/booking') },
  ];

  const tabs = [
    { id: 'invoices', label: 'Invoices', count: stats.invoicesCount || 0 },
    { id: 'clients', label: 'Clients', count: stats.clientsCount || 0 },
    { id: 'agreements', label: 'Contracts', count: stats.agreementsCount || 0 },
  ];

  const sortOptions = [
    { value: 'date', label: 'Most recent' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'amount', label: 'Highest amount' },
    { value: 'amount-low', label: 'Lowest amount' },
    { value: 'name', label: 'A → Z' },
    { value: 'name-desc', label: 'Z → A' },
    { value: 'status-paid', label: 'Paid first' },
    { value: 'status-due', label: 'Due first' },
  ];

  return (
    <div className="h-full flex flex-col relative overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0">

        {/* Header area - Quick navigation + Search */}
        <div className="px-4 lg:px-6 pt-4 lg:pt-5 pb-3 lg:pb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 lg:gap-2">
              {quickActions.map((action, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={action.action}
                  className="flex items-center justify-center gap-2 px-3 py-2 lg:px-4 lg:py-2.5 rounded-xl bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-300 hover:text-brand hover:border-brand/30 hover:shadow-sm transition-all text-sm font-medium"
                >
                  <action.icon className="w-4 h-4 lg:w-5 lg:h-5" />
                  <span className="hidden sm:inline">{action.label}</span>
                </motion.button>
              ))}
            </div>

            {/* Search + Profile */}
            <div className="flex items-center gap-2">
              {/* Search icon / expanded search */}
              <div className="relative">
                <AnimatePresence mode="wait">
                  {showSearch ? (
                    <motion.div
                      key="search-input"
                      initial={{ width: 40, opacity: 0 }}
                      animate={{ width: 240, opacity: 1 }}
                      exit={{ width: 40, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center"
                    >
                      <div className="relative w-full">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          autoFocus
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onBlur={() => !searchQuery && setShowSearch(false)}
                          placeholder="Search..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-brand/40 transition-colors"
                        />
                      </div>
                    </motion.div>
                  ) : (
                    <motion.button
                      key="search-icon"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowSearch(true)}
                      className="w-9 h-9 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <MagnifyingGlassIcon className="w-5 h-5" />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  {currentUser?.photoURL ? (
                    <img
                      src={currentUser.photoURL}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-900 dark:bg-brand flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {currentUser?.displayName?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </button>

                <AnimatePresence>
                  {showProfileMenu && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowProfileMenu(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-xl border border-gray-100 dark:border-[#333] overflow-hidden z-50"
                      >
                        {/* User info header */}
                        <div className="p-4 border-b border-gray-100 dark:border-[#333]">
                          <div className="flex items-center gap-3">
                            {currentUser?.photoURL ? (
                              <img
                                src={currentUser.photoURL}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-900 dark:bg-brand flex items-center justify-center">
                                <span className="text-sm font-medium text-white">
                                  {currentUser?.displayName?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {currentUser?.displayName || 'User'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {currentUser?.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Menu items */}
                        <div className="p-2">
                          <button
                            onClick={() => {
                              setShowProfileMenu(false);
                              navigate('/app/profile');
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Your Profile
                          </button>
                          <button
                            onClick={() => {
                              setShowProfileMenu(false);
                              navigate('/app/settings');
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Settings
                          </button>
                        </div>

                        {/* Sign out */}
                        <div className="p-2 border-t border-gray-100 dark:border-[#333]">
                          <button
                            onClick={async () => {
                              setShowProfileMenu(false);
                              try {
                                await signOut(auth);
                                navigate('/login');
                              } catch (error) {
                                console.error('Sign out error:', error);
                              }
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Sign out
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Tab navigation - subtle */}
        <div className="px-4 lg:px-6 pb-2 flex-shrink-0">
          <div className="flex items-center gap-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-3 py-1.5 text-xs transition-all ${
                  activeTab === tab.id
                    ? 'text-gray-600 dark:text-gray-300'
                    : 'text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute inset-0 bg-gray-100/50 dark:bg-white/5 rounded-md"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{tab.label}</span>
                {tab.count > 0 && (
                  <span className="relative z-10 ml-1 text-[9px] text-gray-300/70 dark:text-gray-600">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content area - fills available space, pb-24 accounts for chat input */}
        <div className="flex-1 px-4 lg:px-6 pb-24 min-h-0 flex flex-col">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-1 min-h-0 bg-gray-50/50 dark:bg-[#0D0D0D] rounded-xl border border-gray-100 dark:border-[#222] flex flex-col overflow-hidden"
          >
            <AnimatePresence mode="wait">
              {activeTab === 'invoices' && (
                <motion.div
                  key="invoices"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-h-0 flex flex-col"
                >
                  {loading.invoices ? (
                    <SkeletonLoader />
                  ) : documents.invoices?.length > 0 ? (
                    <div className="flex-1 min-h-0 flex flex-col">
                      {/* Header bar - minimal, shows selection actions when needed */}
                      <div className="px-5 py-2.5 border-b border-gray-100 dark:border-[#222] flex items-center justify-between flex-shrink-0">
                        <AnimatePresence mode="wait">
                          {selectedInvoices.size > 0 ? (
                            <motion.div
                              key="selection-actions"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="flex items-center gap-4"
                            >
                              <span className="text-xs text-gray-500 dark:text-gray-400">{selectedInvoices.size} selected</span>
                              <button
                                onClick={() => setSelectedInvoices(new Set())}
                                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              >
                                Clear
                              </button>
                              <button
                                onClick={deleteSelectedInvoices}
                                disabled={isDeleting}
                                className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                              >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                              </button>
                            </motion.div>
                          ) : (
                            <motion.span
                              key="count"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="text-xs text-gray-400 dark:text-gray-500"
                            >
                              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                              {searchQuery && ` matching "${searchQuery}"`}
                            </motion.span>
                          )}
                        </AnimatePresence>

                        {/* Sort dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowSortMenu(!showSortMenu)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-white/5 rounded-lg transition-all"
                          >
                            <span>{sortOptions.find(s => s.value === sortBy)?.label}</span>
                            <ChevronDownIcon className={`w-3 h-3 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {showSortMenu && (
                              <>
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="fixed inset-0 z-10"
                                  onClick={() => setShowSortMenu(false)}
                                />
                                <motion.div
                                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                                  transition={{ duration: 0.15 }}
                                  className="absolute right-0 top-full mt-1 bg-white dark:bg-[#1A1A1A] rounded-xl shadow-lg border border-gray-100 dark:border-[#333] py-1.5 z-20 min-w-[140px] overflow-hidden"
                                >
                                  {sortOptions.map((option, i) => (
                                    <motion.button
                                      key={option.value}
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: i * 0.02 }}
                                      onClick={() => {
                                        setSortBy(option.value);
                                        setShowSortMenu(false);
                                      }}
                                      className={`w-full px-3 py-2 text-left text-xs transition-all flex items-center justify-between ${
                                        sortBy === option.value
                                          ? 'text-brand bg-brand/5 dark:bg-brand/10'
                                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'
                                      }`}
                                    >
                                      <span>{option.label}</span>
                                      {sortBy === option.value && (
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </motion.button>
                                  ))}
                                </motion.div>
                              </>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Invoice list - fills available space */}
                      <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60 dark:divide-[#222]">
                        {filteredInvoices.map((invoice, i) => {
                          const isSelected = selectedInvoices.has(invoice.id);
                          const showCheckboxes = selectedInvoices.size > 0;
                          return (
                            <motion.div
                              key={invoice.id || i}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.03 }}
                              onClick={() => invoice.id && navigate(`/preview-invoice/${invoice.id}`)}
                              className={`flex items-center justify-between px-5 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors cursor-pointer group ${
                                isSelected ? 'bg-brand/[0.03] dark:bg-brand/10' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {/* Checkbox - only visible on hover or when items selected */}
                                <div
                                  className={`transition-all duration-150 ${
                                    showCheckboxes ? 'w-6 opacity-100' : 'w-0 opacity-0 group-hover:w-6 group-hover:opacity-100'
                                  }`}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSelectInvoice(invoice.id);
                                    }}
                                    className="w-6 h-6 flex items-center justify-center -m-1"
                                  >
                                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                                      isSelected
                                        ? 'bg-brand border-brand'
                                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                    }`}>
                                      {isSelected && (
                                        <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </span>
                                  </button>
                                </div>

                                <div className="w-8 h-8 rounded-full bg-gray-100/80 dark:bg-[#2A2A2A] flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                                  {(invoice.clientName || 'C')[0].toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {invoice.clientName || 'Client'}
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                    {invoice.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || 'Recent'}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  ${(invoice.total || invoice.price || invoice.amount || 0).toLocaleString()}
                                </span>
                                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                                  invoice.status === 'paid'
                                    ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                                    : 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
                                }`}>
                                  {invoice.status === 'paid' ? 'Paid' : 'Due'}
                                </span>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      icon={DocumentTextIcon}
                      title="No invoices yet"
                      description="Create your first invoice to get started"
                      action={() => setShowInvoiceModal(true)}
                      actionLabel="Create Invoice"
                    />
                  )}
                </motion.div>
              )}

              {activeTab === 'clients' && (
                <motion.div
                  key="clients"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-h-0 flex flex-col"
                >
                  {loading.clients ? (
                    <SkeletonLoader />
                  ) : clients?.length > 0 ? (
                    <div>
                      {/* Header */}
                      <div className="px-6 py-3 border-b border-gray-100/60 dark:border-[#222] flex items-center justify-between">
                        <p className="text-xs text-gray-400 dark:text-gray-500">{clients.length} total clients</p>
                        <button
                          onClick={() => navigate('/app/clients')}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:text-brand transition-colors"
                        >
                          Manage →
                        </button>
                      </div>

                      <div className="divide-y divide-gray-100/60 dark:divide-[#222]">
                        {clients.slice(0, 5).map((client, i) => (
                          <motion.div
                            key={client.id || i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => navigate('/app/clients')}
                            className="flex items-center justify-between px-6 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-9 h-9 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center text-sm font-medium text-brand flex-shrink-0">
                                {(client.name || client.clientName || 'C')[0].toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {client.name || client.clientName || 'Client'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{client.email || 'No email'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {client.invoicesCount || 0} invoices
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowInvoiceModal(true);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-xs text-brand hover:text-brand-hover font-medium transition-opacity"
                              >
                                + Invoice
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      icon={UserGroupIcon}
                      title="No clients yet"
                      description="Add your first client to get started"
                      action={() => navigate('/app/clients')}
                      actionLabel="Add Client"
                    />
                  )}
                </motion.div>
              )}

              {activeTab === 'agreements' && (
                <motion.div
                  key="agreements"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-h-0 flex flex-col"
                >
                  {loading.agreements ? (
                    <SkeletonLoader />
                  ) : documents.agreements?.length > 0 ? (
                    <div>
                      {/* Header */}
                      <div className="px-6 py-3 border-b border-gray-100/60 dark:border-[#222] flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">Awaiting signature</p>
                            <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                              {documents.agreements.filter(a => a.status !== 'signed').length}
                            </p>
                          </div>
                          <div className="w-px h-8 bg-gray-200/60 dark:bg-[#333]" />
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">Signed</p>
                            <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                              {documents.agreements.filter(a => a.status === 'signed').length}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowAgreementModal(true)}
                          className="text-xs text-brand hover:text-brand-hover font-medium transition-colors"
                        >
                          + New
                        </button>
                      </div>

                      <div className="divide-y divide-gray-100/60 dark:divide-[#222]">
                        {documents.agreements.slice(0, 5).map((agreement, i) => (
                          <motion.div
                            key={agreement.id || i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => agreement.id && navigate(`/agreement/${agreement.id}`)}
                            className="flex items-center justify-between px-6 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-9 h-9 rounded-full bg-gray-100/80 dark:bg-[#2A2A2A] flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                                {(agreement.clientName || 'C')[0].toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {agreement.title || 'Contract'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{agreement.clientName || 'Client'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                agreement.status === 'signed'
                                  ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                                  : 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
                              }`}>
                                {agreement.status === 'signed' ? 'Signed' : 'Pending'}
                              </span>
                              {agreement.status !== 'signed' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Could trigger send reminder
                                  }}
                                  className="opacity-0 group-hover:opacity-100 text-xs text-brand hover:text-brand-hover font-medium transition-opacity"
                                >
                                  Send
                                </button>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      icon={DocumentDuplicateIcon}
                      title="No contracts yet"
                      description="Create your first contract to get started"
                      action={() => setShowAgreementModal(true)}
                      actionLabel="Create Contract"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

      </div>

      {/* Floating Create Button - Bottom Right (desktop only, mobile uses FloatingActionButton in AppLayout) */}
      <motion.button
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowCreateOptions(true)}
        className="absolute bottom-6 right-6 hidden lg:flex items-center gap-2 px-5 h-[44px] bg-brand text-white rounded-full hover:bg-brand-hover transition-all text-sm font-medium z-40"
        style={{
          boxShadow: '0 4px 20px rgba(249, 115, 22, 0.3), 0 2px 8px rgba(249, 115, 22, 0.2)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(249, 115, 22, 0.45), 0 4px 16px rgba(249, 115, 22, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(249, 115, 22, 0.3), 0 2px 8px rgba(249, 115, 22, 0.2)';
        }}
      >
        <PlusIcon className="w-5 h-5" />
        <span>Create</span>
      </motion.button>

      {/* Modals */}
      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        user={currentUser}
      />
      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
      />
      <CreateOptionsModal
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
        onCreateEmail={() => {
          setShowCreateOptions(false);
          // Could trigger compose modal if integrated
        }}
        onCreateInvoice={() => {
          setShowCreateOptions(false);
          setShowInvoiceModal(true);
        }}
        onCreateAgreement={() => {
          setShowCreateOptions(false);
          setShowAgreementModal(true);
        }}
      />
    </div>
  );
};

const SkeletonLoader = () => (
  <div className="flex-1 flex flex-col">
    {/* Header skeleton */}
    <div className="px-5 py-2.5 border-b border-gray-100 dark:border-[#222] flex items-center justify-between">
      <div className="h-3 w-20 bg-gray-200/60 dark:bg-gray-700/60 rounded animate-pulse" />
      <div className="h-3 w-24 bg-gray-200/60 dark:bg-gray-700/60 rounded animate-pulse" />
    </div>
    {/* Row skeletons */}
    <div className="divide-y divide-gray-100/60 dark:divide-[#222]">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-9 h-9 rounded-full bg-gray-200/60 dark:bg-gray-700/60 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-32 bg-gray-200/60 dark:bg-gray-700/60 rounded animate-pulse" />
              <div className="h-2.5 w-24 bg-gray-100/80 dark:bg-gray-800/80 rounded animate-pulse" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-3.5 w-16 bg-gray-200/60 dark:bg-gray-700/60 rounded animate-pulse" />
            <div className="h-5 w-12 bg-gray-100/80 dark:bg-gray-800/80 rounded-full animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description, action, actionLabel = 'Create' }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6">
    <div className="w-14 h-14 rounded-2xl bg-brand/5 dark:bg-brand/10 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-brand/60" />
    </div>
    <p className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">{title}</p>
    <p className="text-sm text-gray-400 dark:text-gray-500 mb-5">{description}</p>
    {action && (
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={action}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-full transition-all shadow-sm"
      >
        <PlusIcon className="w-4 h-4" />
        {actionLabel}
      </motion.button>
    )}
  </div>
);

export default DashboardPage;
